import { BatteryLow, HeartPulse, Users, Wind } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { WHATSAPP_URL, trackClick } from "../../lib/site";

const DEMANDS = [
  {
    icon: BatteryLow,
    title: "Exaustão emocional",
    text: "Viver no limite, sem energia para a rotina, como se estivesse no automático.",
    signs: [
      "Cansaço que não passa, mesmo depois de descansar",
      "Irritabilidade e sensação de estar sempre “no máximo”",
      "Dificuldade de sentir prazer no que antes era bom",
    ],
  },
  {
    icon: Users,
    title: "Conflitos familiares",
    text: "Quando a convivência familiar é marcada por conflitos constantes, o acolhimento dá lugar ao estado de alerta. Esse desgaste diário gera ansiedade, exaustão e culpa, comprometendo seu bem-estar e suas relações.",
    signs: [
      "Dificuldade para conversar sem que o diálogo termine em brigas",
      "Sensação de não ser ouvido(a), compreendido(a) e valorizado(a)",
      "Dificuldade em estabelecer limites",
    ],
  },
  {
    icon: HeartPulse,
    title: "Adaptação a doenças crônicas",
    text: "O impacto emocional de conviver com um diagnóstico — seu ou de alguém que você ama.",
    signs: [
      "Medo, incerteza e a sensação de que a vida mudou de repente",
      "Sobrecarga de quem cuida e acaba esquecendo de si",
      "Dificuldade de conciliar tratamento, rotina e relações",
    ],
  },
  {
    icon: Wind,
    title: "Ansiedade e sobrecarga",
    text: "Pensamentos acelerados e preocupação constante, mesmo quando aparentemente está tudo bem.",
    signs: [
      "Mente que não desliga, principalmente à noite",
      "Sinais no corpo: tensão, aperto no peito, falta de ar",
      "Sensação de estar sempre devendo algo",
    ],
  },
];

export const Identification = () => (
  <section id="demandas" data-testid="identification-section" className="py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>Como posso ajudar</Overline>
        <h2 className="mt-6 max-w-3xl font-serif text-3xl font-normal leading-tight tracking-tight text-bark sm:text-4xl lg:text-5xl">
          Você se identifica com alguma dessas{" "}
          <span className="italic text-brand">situações</span>?
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-moss sm:text-lg">
          Cada pessoa chega à terapia por um caminho diferente. Estas são as
          demandas que mais acompanho no consultório:
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {DEMANDS.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.1}>
            <article
              data-testid={`demand-card-${i}`}
              className="group flex h-full flex-col rounded-2xl bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_rgba(45,51,47,0.1)] lg:p-10"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-clay/15 transition-colors duration-500 group-hover:bg-brand/10">
                <item.icon className="h-6 w-6 text-copper transition-colors duration-500 group-hover:text-brand" strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 font-serif text-xl font-medium leading-snug text-bark sm:text-2xl">
                {item.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-moss">{item.text}</p>
              <ul className="mt-6 space-y-3 border-t border-bark/8 pt-6">
                {item.signs.map((sign, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm leading-relaxed text-bark/80">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
                    {sign}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-14 flex flex-col items-start gap-5 rounded-[2rem] border border-clay/30 bg-bone/50 px-8 py-8 sm:flex-row sm:items-center sm:justify-between lg:px-12">
          <p className="max-w-xl font-serif text-xl font-medium italic leading-snug text-bark sm:text-2xl">
            Se alguma dessas situações parece familiar, a terapia pode ser um bom
            começo.
          </p>
          <a
            data-testid="identification-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick("identificacao")}
            className="shrink-0 rounded-full bg-brand px-8 py-4 text-sm font-semibold text-sand shadow-[0_16px_36px_rgba(58,77,57,0.28)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-brand-deep"
          >
            Converse comigo
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
